import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, CheckCircle2 } from 'lucide-react';

/**
 * MOCK Email/Password Login preview.
 * Shows sign-in, sign-up, and forgot-password flows.
 * Does NOT authenticate - purely visual for the user to approve the design.
 */
export default function LoginPreview() {
  const [tab, setTab] = useState('signin'); // signin | signup | forgot
  const [showPassword, setShowPassword] = useState(false);
  const [demoState, setDemoState] = useState(null); // null | 'success' | 'domain-error'

  const [email, setEmail] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!email) return;
    const lower = email.toLowerCase();
    if (
      !lower.endsWith('@iltexas.org') &&
      !lower.endsWith('@iltexasstudent.org')
    ) {
      setDemoState('domain-error');
      return;
    }
    setDemoState('success');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-4 text-center">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full border border-amber-200">
            PREVIEW — email/password mockup (not functional)
          </span>
        </div>

        <Card className="w-full p-8 shadow-xl border border-slate-200">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center mb-3 shadow-lg">
              <svg viewBox="0 0 24 24" className="w-9 h-9 text-yellow-400 fill-current">
                <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4zm0 2.3l6 3v4.7c0 4.4-2.9 8.6-6 9.9-3.1-1.3-6-5.5-6-9.9V7.3l6-3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Eagle Academy WPK8</h1>
            <p className="text-slate-500 text-sm mt-1">
              {tab === 'signin' && 'Sign in to continue'}
              {tab === 'signup' && 'Create your account'}
              {tab === 'forgot' && 'Reset your password'}
            </p>
          </div>

          {/* Tabs (only on signin/signup) */}
          {tab !== 'forgot' && (
            <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-5">
              <button
                onClick={() => { setTab('signin'); setDemoState(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  tab === 'signin' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => { setTab('signup'); setDemoState(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  tab === 'signup' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sign Up
              </button>
            </div>
          )}

          {demoState === 'success' && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">
                Valid email domain. In a real build, you would now be signed in.
              </p>
            </div>
          )}
          {demoState === 'domain-error' && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">
                Only <span className="font-mono">@iltexas.org</span> and{' '}
                <span className="font-mono">@iltexasstudent.org</span> emails are allowed.
              </p>
            </div>
          )}

          {/* SIGN IN */}
          {tab === 'signin' && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm text-slate-700">Email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@iltexas.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="password" className="text-sm text-slate-700">Password</Label>
                  <button
                    type="button"
                    onClick={() => { setTab('forgot'); setDemoState(null); }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-9 pr-9 h-11"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <input type="checkbox" id="remember" className="w-4 h-4 rounded border-slate-300" />
                <label htmlFor="remember" className="text-slate-600 select-none">Keep me signed in</label>
              </div>

              <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
                Sign In
              </Button>
            </form>
          )}

          {/* SIGN UP */}
          {tab === 'signup' && (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first" className="text-sm text-slate-700">First name</Label>
                  <Input id="first" placeholder="Emma" className="mt-1 h-11" required />
                </div>
                <div>
                  <Label htmlFor="last" className="text-sm text-slate-700">Last name</Label>
                  <Input id="last" placeholder="Johnson" className="mt-1 h-11" required />
                </div>
              </div>

              <div>
                <Label htmlFor="email2" className="text-sm text-slate-700">School email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email2"
                    type="email"
                    placeholder="you@iltexas.org or you@iltexasstudent.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11"
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Your role is determined by your email domain.</p>
              </div>

              <div>
                <Label htmlFor="pw2" className="text-sm text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="pw2"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    className="pl-9 pr-9 h-11"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm">
                <input type="checkbox" id="tos" className="mt-0.5 w-4 h-4 rounded border-slate-300" required />
                <label htmlFor="tos" className="text-slate-600 select-none">
                  I agree to the <a className="text-blue-600 hover:underline">Terms</a> &{' '}
                  <a className="text-blue-600 hover:underline">Privacy Policy</a>
                </label>
              </div>

              <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
                Create Account
              </Button>
            </form>
          )}

          {/* FORGOT */}
          {tab === 'forgot' && (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-slate-600">
                Enter your school email and we&apos;ll send you a password reset link.
              </p>
              <div>
                <Label htmlFor="email3" className="text-sm text-slate-700">School email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email3"
                    type="email"
                    placeholder="you@iltexas.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11"
                    required
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
                Send Reset Link
              </Button>

              <button
                type="button"
                onClick={() => { setTab('signin'); setDemoState(null); }}
                className="w-full flex items-center justify-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}

          <p className="text-xs text-slate-400 text-center mt-6 leading-relaxed">
            Only <span className="font-mono">@iltexas.org</span> and{' '}
            <span className="font-mono">@iltexasstudent.org</span> emails are allowed.
          </p>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-4">
          This is a design preview only. Nothing you enter here is saved.
        </p>
      </div>
    </div>
  );
}
